import dates from "componentsv2/tools/dates";
import { useViewModeStore } from "../store/storeView";

const daysOfWeek = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

const getDayName = (date) => {
  return daysOfWeek[date.getDay()];
};

const timeToMinutes = (timeString) => {
  if (!timeString) return 0;
  const [hours, minutes] = timeString.split(":").map(Number);
  return hours * 60 + minutes;
};

const getHourRange = (minStartTime, maxEndTime) => {
  const startMinutes = minStartTime ? timeToMinutes(minStartTime) : 0;
  let endMinutes = maxEndTime ? timeToMinutes(maxEndTime) : 1440; // 24 * 60

  if (endMinutes - startMinutes < 120) {
    endMinutes = startMinutes + 120;
  }

  const startHour = startMinutes > 0 ? Math.floor(startMinutes / 60) : 0;
  const endHour = Math.min(Math.ceil(endMinutes / 60), 24);

  return {
    startHour,
    endHour,
    startMinutes,
    endMinutes,
    numHours: endHour - startHour,
  };
};

function getTimeOff(days, context) {
  const { schedule, minStartTime, maxEndTime } = context;
  if (!schedule) return [];

  const { startHour, endHour } = getHourRange(minStartTime, maxEndTime);
  const startOfDay = `${startHour.toString().padStart(2, "0")}:00:00`;
  const endOfDay = `${endHour.toString().padStart(2, "0")}:00:00`;

  return days.flatMap((item) => {
    const dayName = getDayName(item.day);
    const daySchedule = schedule.find((sch) => sch.day === dayName);

    const dateStr = dates.fmt(item.day, "yyyy-MM-dd");

    if (!daySchedule || !daySchedule.enable) {
      return [
        {
          date: dateStr,
          start_time: `${dateStr} ${startOfDay}`,
          end_time: `${dateStr} ${endOfDay}`,
        },
      ];
    }

    const result = [];

    if (timeToMinutes(daySchedule.start_time) > timeToMinutes(startOfDay)) {
      result.push({
        date: dateStr,
        start_time: `${dateStr} ${startOfDay}`,
        end_time: `${dateStr} ${daySchedule.start_time}`,
      });
    }

    if (timeToMinutes(daySchedule.end_time) < timeToMinutes(endOfDay)) {
      result.push({
        date: dateStr,
        start_time: `${dateStr} ${daySchedule.end_time}`,
        end_time: `${dateStr} ${endOfDay}`,
      });
    }

    return result;
  });
}

const processDataEvents = (data) => {
  const newEvents = new Map();
  const data_ = data ?? [];

  for (const item of data_) {
    const start_date = dates.getTrytonDateTimeLocal2Js(item.start_date, true);
    const end_date = dates.getTrytonDateTimeLocal2Js(item.end_date, true);
    const day = dates.fmt(start_date, "yyyy-MM-dd");
    const professional =
      item?.["professional."]?.rec_name || item?.professional?.rec_name;
    const customer = item?.["customer."]?.rec_name || item?.customer?.rec_name;
    const patient = item?.["patient."]?.rec_name || item?.patient?.rec_name;

    let displayName = customer || patient;
    if (!displayName) {
      const consumer =
        item?.["consumer."]?.rec_name || item?.consumer?.rec_name;
      displayName = consumer || professional || "Cita";
    }

    const value = {
      id: item.id,
      name: displayName,
      customer,
      patient,
      item,
      owner_id: item?.["professional."]?.id,
      start_date,
      end_date,
      contact: item?.["consumer."],
      day,
      professional,
      sale: item.sale,
      state: item.state,
      number: item.number,
      product: item?.["product."]?.name || item?.product?.name,
      room: item?.["room."]?.rec_name || item?.room?.rec_name,
      appointment_type: item?.["appointment_type."],
      description: item.description,
      comment: item.comment,
    };

    const existing = newEvents.get(day) || [];
    existing.push(value);
    newEvents.set(day, existing);
  }
  return newEvents;
};

function getStartAndEndDate(context) {
  const { currentDate, minStartTime, maxEndTime } = context;
  const viewMode = useViewModeStore.getState().viewMode;
  let start;
  let end;

  if (viewMode === "week") {
    start = dates.startOfWeek(currentDate);
    end = dates.endOfWeek(currentDate);
  } else {
    start = dates.startOfWeek(dates.startOfMonth(currentDate));
    end = dates.endOfWeek(dates.endOfMonth(currentDate));
  }
  if (minStartTime) start.setHours(...minStartTime.split(":").map(Number));
  if (maxEndTime) end.setHours(...maxEndTime.split(":").map(Number));

  return { start, end };
}

function getEventDays(context, events) {
  const { schedule, currentDate } = context;
  const disabledDays = schedule
    .filter((day) => !day.enable)
    .map((day) => daysOfWeek.indexOf(day.day));

  const { start, end } = getStartAndEndDate(context);
  const daysCalendar = dates.eachDayOfInterval(start, end);
  const eventDays = [];

  for (const day of daysCalendar) {
    const isCurrentMonth = dates.isSameMonth(day, currentDate);
    const key = dates.fmt(day, "yyyy-MM-dd");
    const isToday = dates.isToday(day);

    eventDays.push({
      day,
      dayString: key,
      isToday,
      isCurrentMonth,
      events: events.get(key) ?? [],
      disabled: disabledDays.includes(day.getDay()),
    });
  }

  return eventDays;
}

function calculateOverlaps(events) {
  if (!events?.length) return [];

  const evs = events
    .map((e) => ({
      ...e,
      _s: new Date(e.start_date).getTime(),
      _e: new Date(e.end_date).getTime(),
    }))
    .sort((a, b) => a._s - b._s);

  const colsEnd = [];
  const assigned = new Map();

  for (const ev of evs) {
    const col = colsEnd.findIndex((end) => ev._s >= end);
    if (col >= 0) {
      colsEnd[col] = ev._e;
      assigned.set(ev.id, col);
    } else {
      colsEnd.push(ev._e);
      assigned.set(ev.id, colsEnd.length - 1);
    }
  }

  return evs.map((ev) => {
    const cols = new Set();
    for (const other of evs) {
      if (other._s < ev._e && other._e > ev._s) {
        cols.add(assigned.get(other.id));
      }
    }
    const sortedCols = [...cols].sort((a, b) => a - b);
    const stackCount = sortedCols.length || 1;
    const stackIndex = Math.max(0, sortedCols.indexOf(assigned.get(ev.id)));

    return {
      ...ev,
      stackIndex,
      stackCount,
    };
  });
}

function groupOverlappingEvents(events, viewMode) {
  if (!events?.length) return [];

  if (viewMode === "day") {
    const groups = new Map();
    for (const ev of events) {
      const key = ev.owner_id || "unknown";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(ev);
    }

    return Array.from(groups.values()).flatMap(calculateOverlaps);
  }

  return calculateOverlaps(events);
}

export default {
  getEventDays,
  getTimeOff,
  getHourRange,
  getStartAndEndDate,
  processDataEvents,
  groupOverlappingEvents,
};
